// What a written split hands back, and the shape the card draws from.
//
// rgip's `presentation.py` puts this on the `activity_end` of a tree write, nested under
// `receipt.card`. Every field was computed inside the write and then thrown away: the
// candidate as applied, the reason the agent gave for it, the options it was ranked
// against, and the two leaves it created. The agent was asked to restate all of it in
// prose instead, which it did on 13 of 24 steps on one build and 3 of 18 on the next.
//
// The candidate travels STRUCTURED, so `candidateConditionTrait` phrases it with the same
// renderer a tree node's own condition uses. That is the whole reason not to render the
// `condition` string rgip also sends: it reads as SQL without being valid SQL, and a
// second implementation of the same English is how two wordings drift apart.
import { SplitCandidateCondition } from '../insights/candidateCondition';

/** A candidate on the card: the structured test, plus how it scored. */
export interface SplitCardCandidate extends SplitCandidateCondition {
  lift?: number;
  coverage_pct?: number;
  true_population?: number;
  /** Which family of attribute it cuts on, so "we took the only firmographic" is visible. */
  dimension?: string;
}

/** One of the two branches the write created. */
export interface SplitCardLeaf {
  node?: number;
  population?: number;
  /** `matched` for the records the condition selects, `rest` for the others. */
  side?: string;
}

export interface SplitCardData {
  kind: 'split';
  tree?: number;
  node?: number;
  /** The node's population BEFORE the cut, which is what the coverage is a share of. */
  population?: number;
  applied: SplitCardCandidate;
  because?: string;
  beat?: SplitCardCandidate[];
  /** Dimension -> how many candidates of it were ranked below the shortlist. */
  never_reached_the_menu?: Record<string, number>;
  new_leaves?: SplitCardLeaf[];
  next?: { node?: number; population?: number };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The card a receipt carries, or null when it carries none.
 *
 * Null is the ordinary case rather than a failure: a read tool has no card, and a write
 * that was REFUSED has no applied candidate. The refusal text tells the agent not to
 * mention that a split was attempted, so drawing something for it would contradict the
 * answer the user is reading.
 *
 * Validated rather than cast. The panel renders whatever the stream sends, and a card
 * without an `applied` candidate would draw a header with nothing under it.
 */
export function splitCardFrom(receipt: unknown): SplitCardData | null {
  if (!isObject(receipt)) return null;
  const card = receipt.card;
  if (!isObject(card) || card.kind !== 'split') return null;
  if (!isObject(card.applied)) return null;
  return card as unknown as SplitCardData;
}

/** `2.2x`, at the precision the engine itself rounds to. */
export function liftText(lift: number | undefined): string | null {
  if (typeof lift !== 'number' || !Number.isFinite(lift)) return null;
  // Two decimals below 1x: a group at 0.14x and one at 0.06x are a very different
  // thing, and one decimal calls them the same. Mirrors rgip's `round_lift`.
  return `${lift >= 1 ? lift.toFixed(1) : lift.toFixed(2)}x`;
}

/** `26%`, whole numbers, because a tenth of a percent of a lead list means nothing. */
export function coverageText(pct: number | undefined): string | null {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return null;
  return `${Math.round(pct)}%`;
}

export function countText(n: number | undefined): string | null {
  return typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString()
    : null;
}
